import { useLocation } from 'react-router-dom';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { associateMetadataFormToResource, deleteMetadataForm, listMetadataForms } from 'src/api/catalog';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { createMetadataFormViewLink, Page } from 'src/routes';
import {
  Box,
  Button,
  ButtonDropdown,
  CollectionPreferences,
  CollectionPreferencesProps,
  Flashbar,
  Header,
  Modal,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { defaultWrapLinesPreference, notificationsPageSizePreference, paginationLabels } from 'src/commons/tables';
import MetadataFormFieldsTable from 'src/components/metadata/metadataFields/metadataFormFields';
import MetadataFormAssociationsTable from 'src/components/metadata/metadataForms/metadataFormAssociations';
import { DeleteMetadataFormRequest } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import { isHCAdmin } from 'src/commons/common';
import { ResourceSelectorModal } from 'src/components/common/resourceSelector/resourceSelectorModal';
import { generateArnFromId } from 'src/components/utils/arnUtil';
import { EmptyState } from 'src/commons/EmptyState';

export interface MetadataFormsProps extends CommonPageProps {
  setContentType: any;
  activeGroup: string;
  match: any;
  activeWorkspace?: any;
}

export const MetaDataForms = (props: MetadataFormsProps) => {
  const { state } = useLocation();
  const [loadingForms, setLoadingForms] = useState(false);
  const [ownerId, setOwnerId] = useState(undefined);
  const [metadataForms, setMetaDataForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(undefined);
  const [formsTableMessage, setFormsTableMessage] = useState('No forms available');
  const [notifications, setNotifications] = useState([]);
  const [deleteFormModalVisible, setDeleteFormModalVisible] = useState(false);
  const [associateMetadataFormModalVisible, setAssociateMetadataFormModalVisible] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 10,
  });

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(metadataForms, {
    filtering: {
      noMatch: '',
      empty: <EmptyState title={formsTableMessage} subtitle='No forms were found.' />,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  const metadataFormColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'metadataFormName',
      header: 'Name',
      cell: (item) => (
        <Link href={createMetadataFormViewLink(item.MetadataFormId, item.OwnerId)}>{item.Name.split()}</Link>
      ),
      minWidth: 200,
      sortingField: 'metadataFormName',
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item) => item.Description,
      minWidth: 200,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => (
        <StatusIndicator type={item.Status == 'Enabled' ? 'success' : 'stopped'}>{item.Status}</StatusIndicator>
      ),
      minWidth: 200,
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item) => item.FormType.replace('Form#', ''),
      minWidth: 200,
    },
  ];

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    const activeGroup = props.activeGroup;
    const activeWorkspace = props.activeWorkspace;
    const ownerId = activeWorkspace ? activeWorkspace.workspaceId : activeGroup;
    setOwnerId(ownerId);
    if (state) {
      setNotification(state.status, state.message);
    }
  }, []);

  useEffect(() => {
    if (ownerId) {
      handleRefresh();
    }
  }, [ownerId]);

  const handleRefresh = async () => {
    setLoadingForms(true);
    setButtonLoading(true);
    setSelectedForm(undefined);
    await getMetaDataFormsForOwner();
  };

  useEffect(() => {
    const { selectedItems } = collectionProps;
    const selectedForm = selectedItems[0];
    if (selectedForm) {
      setSelectedForm(selectedForm);
    }
  }, [collectionProps.selectedItems]);

  const getMetaDataFormsForOwner = async () => {
    setLoadingForms(true);
    setButtonLoading(true);
    try {
      let listMetadataFormsResult = await listMetadataForms({
        OwnerId: ownerId,
      });
      let forms = listMetadataFormsResult.MetadataFormsList;
      while (listMetadataFormsResult.NextToken != undefined) {
        listMetadataFormsResult = await listMetadataForms({
          OwnerId: ownerId,
          NextToken: listMetadataFormsResult.NextToken,
        });
        forms.push(...listMetadataFormsResult.MetadataFormsList);
      }
      setMetaDataForms(forms);
    } catch (err) {
      setFormsTableMessage(`Unable to load forms. ${err.message}`);
    } finally {
      setLoadingForms(false);
      setButtonLoading(false);
    }
  };

  const setNotification = async (header, message) => {
    if (header === 'error') {
      setNotifications([
        {
          header: header,
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  const shouldEnableAdminPermissions = () => {
    if (!selectedForm) return false;
    if (props.activeGroup == selectedForm.OwnerId || props.activeWorkspace?.workspaceId == selectedForm.OwnerId)
      return true;
    if (isHCAdmin(props.activeGroup)) return true;
    return false;
  };

  const getFilterCounterText = (count) => `${count} ${count === 1 ? 'match' : 'matches'}`;

  const metadataFormButtonOptions = () => {
    return (
      <SpaceBetween size={'s'} direction={'horizontal'}>
        <Button variant={'icon'} iconName={'refresh'} onClick={handleRefresh} />
        <ButtonDropdown
          onItemClick={handleMetadataFormButtonAction}
          items={[
            {
              text: 'Edit',
              id: 'editMetadataForm',
              disabled: !shouldEnableAdminPermissions(),
            },
            {
              text: 'Delete',
              id: 'deleteMetadataForm',
              disabled: !shouldEnableAdminPermissions(),
            },
            {
              text: 'Associate',
              id: 'associateMetadataForm',
              disabled: !selectedForm,
            },
          ]}
        >
          Actions
        </ButtonDropdown>
        <Button variant={'primary'} onClick={() => props.setRedirect(Page.CREATE_METADATA_FORM)}>
          Create form
        </Button>
      </SpaceBetween>
    );
  };

  const handleMetadataFormButtonAction = async (e) => {
    if (e.detail.id === 'editMetadataForm') {
      handleEditMetadataForm();
    } else if (e.detail.id === 'deleteMetadataForm') {
      setDeleteFormModalVisible(true);
    } else if (e.detail.id == 'associateMetadataForm') {
      setAssociateMetadataFormModalVisible(true);
    }
  };

  const handleEditMetadataForm = async () => {
    if (selectedForm !== undefined) {
      props.setRedirect(Page.EDIT_METADATA_FORM, false, {
        metadataFormId: selectedForm.MetadataFormId,
      });
    } else {
      setNotification('error', 'Please select a form to edit');
    }
  };

  const handleDeleteMetadataForm = async () => {
    setButtonLoading(true);
    try {
      let deleteMetadataFormResult = await deleteMetadataForm({
        OwnerId: ownerId,
        MetadataFormId: selectedForm.MetadataFormId,
      } as DeleteMetadataFormRequest);
      if (deleteMetadataFormResult.Message == 'Success') {
        await setNotification('success', 'Successfully deleted the form.');
      } else {
        await setNotification('error', `Failed to delete the form. ${deleteMetadataFormResult.Message}`);
      }
    } catch (err) {
      setNotification('error', `Error in deleting the form. ${err.message}`);
    } finally {
      setButtonLoading(false);
      setDeleteFormModalVisible(false);
      await handleRefresh();
    }
  };

  const handleAssociateMetadataForm = async (resourceId: string) => {
    try {
      let resourceArn = generateArnFromId(resourceId);
      let associateMetadataFormToResourceRequest = {
        MetadataFormId: selectedForm?.MetadataFormId,
        OwnerId: selectedForm?.OwnerId,
        ResourceArn: resourceArn,
      };
      const associateMetadataFormToResourceResult = await associateMetadataFormToResource(
        associateMetadataFormToResourceRequest,
      );
      if (associateMetadataFormToResourceResult.Message == 'Success') {
        setNotification('success', 'Success');
      } else {
        setNotification('error', `Failure in associating form: ${associateMetadataFormToResourceResult.Message}`);
      }
    } catch (err) {
      setNotification('error', `Error in associating form: ${err.message}`);
    } finally {
      setAssociateMetadataFormModalVisible(false);
    }
  };

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      <Flashbar items={notifications} />
      {deleteFormModalVisible && (
        <Modal
          visible={deleteFormModalVisible}
          header={[`Confirm deletion`]}
          onDismiss={() => setDeleteFormModalVisible(false)}
          footer={
            <Box float='right'>
              <SpaceBetween direction='horizontal' size='xs'>
                <Button variant='primary' loading={buttonLoading} onClick={handleDeleteMetadataForm}>
                  Confirm
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <TextContent>
            Are you sure you want to delete form <strong>'{selectedForm?.Name}'</strong>?
            <br />
            <br />
            <strong>
              Please delete all the corresponding fields and associated resources before deleting the form.
            </strong>
          </TextContent>
        </Modal>
      )}
      {associateMetadataFormModalVisible && (
        <ResourceSelectorModal
          activeGroup={props.activeGroup}
          activeWorkspace={props.activeWorkspace}
          visible={associateMetadataFormModalVisible}
          onSelect={handleAssociateMetadataForm}
          close={() => setAssociateMetadataFormModalVisible(false)}
          disableGroupAndWorkspaceSelection={true}
          disableTagSelection={true}
          disableMetadataFormSelection={true}
        />
      )}
      <SpaceBetween size='l'>
        <Table
          {...collectionProps}
          selectionType='single'
          loadingText='Loading forms...'
          loading={loadingForms}
          columnDefinitions={metadataFormColumnDefinitions}
          items={items}
          wrapLines={preferences.wrapLines}
          empty={<EmptyState title={'No forms'} subtitle={'No metadata forms to display.'} />}
          filter={
            <TextFilter
              {...filterProps}
              filteringAriaLabel='Filter resources'
              filteringPlaceholder='Find resources'
              countText={getFilterCounterText(filteredItemsCount)}
            />
          }
          resizableColumns={true}
          header={
            <Header
              counter={`(${items.length})`}
              actions={
                <SpaceBetween size={'s'} direction={'horizontal'}>
                  {metadataFormButtonOptions()}
                </SpaceBetween>
              }
            >
              Metadata forms
            </Header>
          }
          preferences={
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={notificationsPageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          }
          pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        />
        {selectedForm && (
          <MetadataFormFieldsTable
            form={selectedForm}
            ownerId={ownerId}
            activeGroup={props.activeGroup}
            activeWorkspace={props.activeWorkspace}
            setNotification={setNotification}
          />
        )}
        {selectedForm && (
          <MetadataFormAssociationsTable form={selectedForm} ownerId={ownerId} setNotification={setNotification} />
        )}
      </SpaceBetween>
    </SpaceBetween>
  );
};

export default MetaDataForms;
